/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
'use strict'
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Form, Row, Col } from 'react-bootstrap';
import { useState } from 'react';

function GeoMapFingerDialog({ show, setShow, geoCommand, setGeoCommand }) {
    const handleClose = () => {
        setShow(false);
    }

    const handleChange = (e) => {
        const { id, value } = e.target;
        setGeoCommand({
            ...geoCommand,
            [id]: value
        });
    };
    // Every filter field has to be spread back in, otherwise editing one of
    // them silently drops the rest of the filter
    const handleFilterChange = (e) => {
        const { id, value } = e.target;
        setGeoCommand({
            ...geoCommand,
            filter: {
                ...geoCommand.filter,
                [id]: value
            }
        });
    };
    const handleCheckChange = (e) => {
        const { id, checked } = e.target;
        setGeoCommand({
            ...geoCommand,
            [id]: checked
        });
    };
    const handleCheckFilterChange = (e) => {
        const { id, checked } = e.target;
        setGeoCommand({
            ...geoCommand,
            filter: {
                ...geoCommand.filter,
                [id]: checked
            }
        });
    };
    
    
    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Map Fingerprints</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="search">Search</Form.Label>
                        <Form.Control 
                            type="text" 
                            id="search" 
                            placeholder="Enter search term"
                            value={geoCommand.filter.search}
                            onChange={handleFilterChange}
                        />
                        <Form.Text className="text-muted">
                            Limit to fingerprints matching this text (in description, tag, IP, etc.)
                        </Form.Text>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Check 
                            type="checkbox"
                            id="allLayers"
                            label="All Layers"
                            checked={geoCommand.filter.allLayers}
                            onChange={handleCheckFilterChange}
                            className="mb-2"
                        />
                        <Form.Text className="text-muted mb-2">
                            Include fingerprints from all layers
                        </Form.Text>
                        {!geoCommand.filter.allLayers && (
                            <Row>
                                <Col>
                                    <Form.Label htmlFor="layer">Layer</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        id="layer" 
                                        min="0" 
                                        step="1"
                                        value={geoCommand.filter.layer}
                                        onChange={handleFilterChange}
                                    />
                                    <Form.Text className="text-muted">
                                        Only include fingerprints from this layer
                                    </Form.Text>
                                </Col>
                            </Row>
                        )}
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="selected">Working Set</Form.Label>
                        <Form.Select 
                            id="selected" 
                            value={geoCommand.filter.selected}
                            onChange={handleFilterChange}
                        >
                            <option value="all">---</option>
                            <option value="inws">In Working Set</option>
                            <option value="notinws">Not In Working Set</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Limit to fingerprints in or out of the the working set
                        </Form.Text>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                            <Form.Label htmlFor="tag" className="me-2">Tag</Form.Label>
                            <div className="position-relative">
                                <span 
                                    className="d-inline-flex justify-content-center align-items-center bg-secondary text-white rounded-circle mb-1"
                                    style={{ width: '18px', height: '18px', fontSize: '12px', cursor: 'pointer' }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const helpEl = document.getElementById('tag-help');
                                        if (helpEl) {
                                            helpEl.style.display = helpEl.style.display === 'none' ? 'block' : 'none';
                                        }
                                    }}
                                >
                                    ?
                                </span>
                                <div 
                                    id="tag-help" 
                                    className="position-absolute bg-dark p-2 border border-success rounded shadow-sm"
                                    style={{ 
                                        display: 'none', 
                                        width: '960px', 
                                        zIndex: 100, 
                                        left: '25px',
                                        bottom: '5px'
                                    }}
                                >
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white position-absolute"
                                        style={{ top: '2px', right: '4px' }}
                                        aria-label="Close"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const helpEl = document.getElementById('tag-help');
                                            if (helpEl) {
                                                helpEl.style.display = 'none';
                                            }
                                        }}
                                    ></button>
                                    <h4>Tag Filter Help</h4>
                                    <p>
                                    Fingerprints have a JSON tag object that is used by modules/plugins
                                    for markup and diagnostics. Filtering on tags allows full access
                                    to the detailed data gathered (such as TLS details, 
                                    robots.txt, etc). 
                                    </p>
                                    <p>
                                    If the tag value field is empty, then fingerprints with any value in the tag path target are included.

                                    If the tag value field is non-empty it is treated as
                                    a regular expression and executed against the value
                                    extracted from the tag path. If the regexp matches
                                    the fingerprint is included.
                                    </p>
                                    <p>
                                    The path uses a simplified JSONPath-like syntax. It must begin with
                                    a '$' character, which represents the root of the JSON object,
                                    this is followed by zero or more instances of 
                                    "<code>.obj</code>" or "<code>[idx]</code>".
                                    </p>
                                    <p>
                                    Look at a fingerprint's 'res_tags' field in the JSON browser to exlore the markup. Look at the individual AI ops to see what tags they generate.
                                    </p>
                                    <h5>Examples:</h5>
                                    <ul>
                                        <li><code>$.country_code</code> - fingerprints with a non-empty country code</li>
                                        <li><code>$.country_code</code> - with Tag Value set to <code>^CN$</code> - fingerprints with country code exactly "CN"</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <Form.Control 
                            type="text" 
                            id="tag" 
                            placeholder="Enter tag"
                            value={geoCommand.filter.tag}
                            onChange={handleFilterChange}
                        />
                        <Form.Text className="text-muted">
                            Filter by tag 
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="tagValue">Tag Value</Form.Label>
                        <Form.Control 
                            type="text" 
                            id="tagValue" 
                            placeholder="Enter tag value"
                            value={geoCommand.filter.tagValue}
                            onChange={handleFilterChange}
                        />
                        <Form.Text className="text-muted">
                            Optional regular expression to match against the tag value
                        </Form.Text>
                    </Form.Group>
                    
                    <Form.Check 
                        type="checkbox"
                        id="businessLocations"
                        label="Business Locations"
                        checked={geoCommand.businessLocations}
                        onChange={handleCheckChange}
                        className="mb-3"
                    />
                    <Form.Text className="text-muted mb-3">
                        Include business location fingerprints
                    </Form.Text>
                    
                    <Form.Check 
                        type="checkbox"
                        id="ipGeolocations"
                        label="IP Geolocations"
                        checked={geoCommand.ipGeolocations}
                        onChange={handleCheckChange}
                        className="mb-1"
                    />
                    <Form.Text className="text-muted mb-2">
                        Include IP geolocation fingerprints
                    </Form.Text>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={() => {
                    handleChange({ target: { id: 'cmd', value: 'add' }});
                    handleClose();
                }}>
                    Add
                </Button>
                <Button variant="primary" onClick={() => {
                    handleChange({ target: { id: 'cmd', value: 'set'}});
                    handleClose();
                }}>
                    Set
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default GeoMapFingerDialog;